import { Head, Link, router } from "@inertiajs/react"
import { Boxes, ChevronRight, Gauge } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Select } from "@/components/ui/select"
import {
  categoryLabel,
  OwnBadge,
  Pagination,
  type PageMeta,
  SearchForm,
} from "./shared"

type ProductRow = {
  id: number
  canonical_name: string
  pack_size: string | null
  star_product_code: string | null
  is_own: boolean
  status: string | null
  brand: string | null
  manufacturer: string | null
  tracked_category: string | null
}

type BrandOption = { id: number; name: string; is_own: boolean }

type Props = {
  products: ProductRow[]
  pagination: PageMeta
  filters: { search: string | null; owned: string | null; brand_id: number | null; category: string | null; status: string | null }
  options: { brands: BrandOption[]; categories: string[]; statuses: string[] }
  stats: { total: number; own: number; competitors: number }
}

const DESCRIPTION =
  "Performa Produk — telusuri tiap produk kanonik, lalu buka untuk melihat tren hasil, konteks pendorong (harga·promo·diskon), dan perbandingan own-vs-kompetitor secara deterministik."

export default function Index({ products, pagination, filters, options, stats }: Props) {
  const path = "/admin/product-performance"
  const query = {
    search: filters.search,
    owned: filters.owned,
    brand_id: filters.brand_id,
    category: filters.category,
    status: filters.status,
  }

  const update = (patch: Record<string, string | number | undefined | null>) => {
    const q: Record<string, string> = {}
    Object.entries({ ...query, ...patch }).forEach(([k, v]) => {
      if (v != null && v !== "") q[k] = String(v)
    })
    router.get(path, q, { preserveState: true, preserveScroll: true, replace: true })
  }

  return (
    <>
      <Head title="Performa Produk">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Performa Produk" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell width="wide">
        <div className="border-b border-hairline pb-6">
          <div className="flex items-center gap-3">
            <Gauge className="h-6 w-6 text-accent" aria-hidden="true" />
            <h1>Performa Produk</h1>
          </div>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span className="inline-flex items-center gap-1">
              <Boxes className="h-3.5 w-3.5" aria-hidden="true" /> {stats.total} produk
            </span>
            <span>{stats.own} sendiri</span>
            <span>{stats.competitors} kompetitor</span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <SearchForm path={path} query={query} initial={filters.search ?? ""} placeholder="Nama produk, kode STAR, merek" />
          <div className="min-w-[150px]">
            <label htmlFor="owned">Kepemilikan</label>
            <Select id="owned" className="mt-1" value={filters.owned ?? ""} onChange={(e) => update({ owned: e.target.value, brand_id: "" })}>
              <option value="">Semua</option>
              <option value="true">Sendiri</option>
              <option value="false">Kompetitor</option>
            </Select>
          </div>
          <div className="min-w-[170px]">
            <label htmlFor="brand">Merek</label>
            <Select id="brand" className="mt-1" value={filters.brand_id ?? ""} onChange={(e) => update({ brand_id: e.target.value })}>
              <option value="">Semua merek</option>
              {options.brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[160px]">
            <label htmlFor="category">Kategori</label>
            <Select id="category" className="mt-1" value={filters.category ?? ""} onChange={(e) => update({ category: e.target.value })}>
              <option value="">Semua kategori</option>
              {options.categories.map((c) => (
                <option key={c} value={c}>
                  {categoryLabel(c)}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[140px]">
            <label htmlFor="status">Status</label>
            <Select id="status" className="mt-1" value={filters.status ?? ""} onChange={(e) => update({ status: e.target.value })}>
              <option value="">Semua status</option>
              {options.statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">Tidak ada produk yang cocok dengan filter.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Produk</th>
                    <th scope="col" className="px-4 py-3 font-medium">Merek</th>
                    <th scope="col" className="px-4 py-3 font-medium">Kategori</th>
                    <th scope="col" className="px-4 py-3 font-medium">Kode STAR</th>
                    <th scope="col" className="px-4 py-3 font-medium">Penanda</th>
                    <th scope="col" className="w-10 px-4 py-3" aria-label="Buka" />
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link href={`${path}/${p.id}`} className="font-medium">
                          {p.canonical_name}
                        </Link>
                        {p.pack_size && <span className="ml-2 text-xs text-ink-muted">{p.pack_size}</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-body">{p.brand ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-body">{categoryLabel(p.tracked_category) ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-muted">
                        {p.star_product_code ? <span className="font-mono text-xs">{p.star_product_code}</span> : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <OwnBadge isOwn={p.is_own} />
                      </td>
                      <td className="px-4 py-3 text-ink-muted">
                        <Link href={`${path}/${p.id}`} aria-label={`Buka ${p.canonical_name}`}>
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {products.map((p) => (
                <Link key={p.id} href={`${path}/${p.id}`} className="rounded-lg border border-hairline bg-surface p-4">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-ink-body">{p.canonical_name}</span>
                    <OwnBadge isOwn={p.is_own} />
                  </div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {[p.brand, categoryLabel(p.tracked_category), p.pack_size].filter(Boolean).join(" · ") || "—"}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <Pagination meta={pagination} path={path} query={query} />
      </AppShell>
    </>
  )
}
